import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, SlidersHorizontal, UsersRound } from "lucide-react";
import NewSegmentModal from "./new-segment-modal";
import SegmentBuilder from "./segment-builder";
import Pagination from "../../../components/pagination";
import { useCreateSegment, useGetSegment, useListSegments, useUpdateSegmentRules, SegmentsListResponse } from "./api";
import { ContactScope, RuleTree, Segment, SegmentApiRecord, mapRecordToSegment } from "./types";

type View = { name: "list" } | { name: "builder"; segment: Segment };

const PAGE_SIZE = 10;

const SegmentsPage: React.FC = () => {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [showNewSegmentModal, setShowNewSegmentModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState<View>({ name: "list" });
  const [openingSegmentId, setOpeningSegmentId] = useState<string>("");

  const { makeRequest: getAllSegments } = useListSegments();
  const { makeRequest: createSegment } = useCreateSegment();
  // Fixed base path — the id is passed per-call via makeRequest's pathParam,
  // so this doesn't need to be re-created when the clicked segment changes.
  const { makeRequest: getSegmentById } = useGetSegment();
  // Fixed base path — the id goes through makeRequest's pathParam per call.
  const { makeRequest: updateSegmentRules } = useUpdateSegmentRules();


  const loadSegments = async (page = currentPage, showPaginationLoader = false) => {
    if (showPaginationLoader) setIsPaginationLoading(true);
    else setLoading(true);
    try {
      const params: Record<string, unknown> = { page, limit: pageSize };
      if (debouncedSearchQuery.trim()) params.search = debouncedSearchQuery.trim();
      const [response] = await getAllSegments(undefined, params);
      const data: SegmentsListResponse = response || { total: 0, pages: 1, page: 1, limit: pageSize, data: [] };
      const records: SegmentApiRecord[] = Array.isArray(data.data) ? data.data : [];
      setSegments(records.map(mapRecordToSegment));
      setTotalPages(Math.max(1, data.pages || 1));
    } catch {
      setSegments([]);
    } finally {
      setLoading(false);
      setIsPaginationLoading(false);
    }
  };

  // Debounce search input before it hits the backend.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchQuery(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery]);

  useEffect(() => {
    loadSegments(currentPage, currentPage !== 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedSearchQuery]);

  // Pick up a segment draft left by the Broadcast Report's "Add to Segment"
  // buttons (POST /segments/campaign/{id} response: { name, rule_tree }) —
  // same sessionStorage handoff pattern used elsewhere for cross-page state,
  // since that page navigates here before this component mounts.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("pendingSegmentDraft");
      if (raw) {
        sessionStorage.removeItem("pendingSegmentDraft");
        const draft = JSON.parse(raw);
        if (draft?.name) {
          setView({ name: "builder", segment: mapRecordToSegment(draft) });
        }
      }
    } catch {
      // malformed/unavailable sessionStorage — no-op, segments list just opens normally.
    }
  }, []);

  const handleCreateSegment = async (name: string) => {
    setCreating(true);
    try {
      const [response, status] = await createSegment({ name, source_scope: "all" });
      if (status < 200 || status >= 300) {
        toast.error(response?.detail || response?.message || "Failed to create segment");
        return;
      }
      const record: SegmentApiRecord = response?.id ? response : response?.data || response?.segment || response;
      const segment = mapRecordToSegment(record);
      setSegments((prev) => [segment, ...prev]);
      setShowNewSegmentModal(false);
      setView({ name: "builder", segment });
    } finally {
      setCreating(false);
    }
  };

  const handleOpenSegment = async (segment: Segment) => {
    setOpeningSegmentId(segment.id);
    try {
      const [response, status] = await getSegmentById(undefined, undefined, segment.id);
      if (status < 200 || status >= 300) {
        toast.error(response?.detail || response?.message || "Failed to load segment details");
        return;
      }
      const record: SegmentApiRecord = response?.data || response;
      setView({ name: "builder", segment: mapRecordToSegment(record) });
    } finally {
      setOpeningSegmentId("");
    }
  };

  const handleSaveSegment = async (ruleTree: RuleTree, scope: ContactScope, name: string) => {
    if (view.name !== "builder") return;
    setSaving(true);
    try {
      const [response, status] = await updateSegmentRules(
        { rule_tree: ruleTree, source_scope: scope, name },
        undefined,
        `${view.segment.id}/rules`,
      );
      if (status < 200 || status >= 300) {
        toast.error(response?.detail || response?.message || "Failed to save segment rules");
        return;
      }
      toast.success(`"${name}" segment saved.`);
      setView({ name: "list" });
      loadSegments(currentPage);
    } finally {
      setSaving(false);
    }
  };

  if (view.name === "builder") {
    return (
      <SegmentBuilder
        segment={view.segment}
        onBack={() => setView({ name: "list" })}
        onSave={handleSaveSegment}
        saving={saving}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-transparent p-4 sm:p-6 space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">Segments</h1>
        <p className="text-sm text-gray-500 dark:text-white dark:font-medium mt-1">Create new segments from your contacts for your broadcasts</p>
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 sm:p-5">
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <button
            onClick={() => setShowNewSegmentModal(true)}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors whitespace-nowrap"
          >
            <UsersRound size={16} /> Create New Segment
          </button>
          <div className="relative w-full max-w-xs">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search Segment"
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
            />
          </div>
          <button className="text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg px-3.5 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors whitespace-nowrap">
            Select All
          </button>
          <button
            onClick={() => toast.info("Filters are coming soon.")}
            className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg px-3.5 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors whitespace-nowrap"
          >
            <SlidersHorizontal size={14} /> Filters
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-800">
                <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-white">Segment Name</th>
                <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-white">Created On</th>
                <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-white">Contacts</th>
                <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-white">Valid Contacts</th>
                <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-white">Invalid Contacts</th>
                <th className="text-left py-2.5 px-2 text-xs font-semibold text-gray-500 dark:text-white">Used In</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-sm text-[#7B7B7B] font-semibold dark:text-white dark:font-medium">
                    Loading segments…
                  </td>
                </tr>
              ) : segments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-sm text-[#7B7B7B] font-semibold dark:text-white dark:font-medium">
                    {debouncedSearchQuery ? "No segments match your search." : "No segments yet."}
                  </td>
                </tr>
              ) : (
                segments.map((segment) => (
                  <tr
                    key={segment.id}
                    onClick={() => (openingSegmentId ? undefined : handleOpenSegment(segment))}
                    className={`border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50/60 dark:hover:bg-gray-800/50 transition-colors cursor-pointer ${
                      openingSegmentId === segment.id ? "opacity-50" : ""
                    }`}
                  >
                    <td className="py-3 px-2 text-sm font-medium text-gray-800 dark:text-gray-100">
                      {segment.name}
                      {openingSegmentId === segment.id && <span className="ml-2 text-xs text-[#7B7B7B] font-semibold dark:text-white dark:font-medium">Loading…</span>}
                    </td>
                    <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{segment.createdOn}</td>
                    <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-400">{segment.contacts}</td>
                    <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-400">{segment.validContacts.toLocaleString()}</td>
                    <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-400">{segment.invalidContacts.toLocaleString()}</td>
                    <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{segment.usedIn}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="pt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            isLoading={isPaginationLoading}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      <NewSegmentModal
        isOpen={showNewSegmentModal}
        onClose={() => setShowNewSegmentModal(false)}
        onCreate={handleCreateSegment}
        isSubmitting={creating}
      />
    </div>
  );
};

export default SegmentsPage;
